import type { BlobRef, JsonValue, StoredDocument } from "@tchoma/storage";

export const INBOX_DOCUMENT_TYPE = "inbox-entry";
export const INBOX_TITLE_BYTES_MAX = 512;
export const INBOX_BODY_BYTES_MAX = 32_768;
export type InboxKind = "text" | "url" | "image" | "file";

export interface InboxAttachment extends Record<string, JsonValue> {
  blobId: string;
  name: string;
  mime: string;
  size: number;
  hash: string;
  createdAt: string;
}

export interface InboxEntryData extends Record<string, JsonValue> {
  kind: InboxKind;
  title: string;
  body: string;
  sourceUrl: string | null;
  attachment: InboxAttachment | null;
  archived: boolean;
}

export type InboxEntry = StoredDocument<InboxEntryData>;

export interface InboxCaptureDraft {
  text?: string;
  file?: Blob & { name?: string };
  title?: string;
}

export function inferInboxKind(text: string, file?: Blob): InboxKind {
  if (file) return file.type.startsWith("image/") ? "image" : "file";
  return parseWebUrl(text) === null ? "text" : "url";
}

export function createInboxEntryData(
  draft: InboxCaptureDraft,
  attachment: BlobRef | null,
): InboxEntryData {
  const body = (draft.text ?? "").trim();
  const kind = inferInboxKind(body, draft.file);
  const sourceUrl = kind === "url" ? parseWebUrl(body) : null;
  const fallbackTitle = kind === "url"
    ? new URL(sourceUrl!).hostname.replace(/^www\./, "")
    : kind === "image" || kind === "file"
      ? draft.file?.name || (kind === "image" ? "Image" : "File")
      : body.split(/\s+/u).slice(0, 8).join(" ") || "Untitled note";
  const title = draft.title?.trim() || fallbackTitle;
  return parseInboxEntryData({
    kind,
    title,
    body,
    sourceUrl,
    attachment: attachment === null ? null : {
      blobId: attachment.id,
      name: draft.file?.name || title,
      mime: attachment.mime,
      size: attachment.size,
      hash: attachment.hash,
      createdAt: attachment.createdAt,
    },
    archived: false,
  });
}

export function parseInboxEntryData(value: unknown): InboxEntryData {
  if (!isRecord(value)) throw new Error("Inbox entry must be an object.");
  assertExactKeys(value, ["kind", "title", "body", "sourceUrl", "attachment", "archived"], "Inbox entry");
  if (!isInboxKind(value.kind)) throw new Error("Inbox entry kind is invalid.");
  const title = boundedText(value.title, "title", INBOX_TITLE_BYTES_MAX, false);
  const body = boundedText(value.body, "body", INBOX_BODY_BYTES_MAX, true);
  const sourceUrl = value.sourceUrl === null ? null : parseRequiredWebUrl(value.sourceUrl);
  const attachment = value.attachment === null ? null : parseInboxAttachment(value.attachment);
  if (typeof value.archived !== "boolean") throw new Error("Inbox entry archived state is invalid.");
  if (value.kind === "url" && sourceUrl === null) throw new Error("URL entries require a source URL.");
  if ((value.kind === "image" || value.kind === "file") && attachment === null) {
    throw new Error("Image and file entries require an attachment.");
  }
  return { kind: value.kind, title, body, sourceUrl, attachment, archived: value.archived };
}

export function matchesInboxQuery(entry: InboxEntry, query: string): boolean {
  const normalized = query.trim().toLocaleLowerCase();
  if (!normalized) return true;
  return [entry.data.title, entry.data.body, entry.data.sourceUrl ?? "", entry.data.attachment?.name ?? ""]
    .join("\n")
    .toLocaleLowerCase()
    .includes(normalized);
}

export function parseWebUrl(value: string): string | null {
  try {
    const url = new URL(value.trim());
    return url.protocol === "https:" || url.protocol === "http:" ? url.href : null;
  } catch {
    return null;
  }
}

export function attachmentRef(entry: InboxEntry): BlobRef | null {
  const attachment = entry.data.attachment;
  if (!attachment) return null;
  return {
    id: attachment.blobId,
    mime: attachment.mime,
    size: attachment.size,
    hash: attachment.hash,
    createdAt: attachment.createdAt,
  };
}

function parseInboxAttachment(value: unknown): InboxAttachment {
  if (!isRecord(value)) throw new Error("Inbox attachment must be an object.");
  assertExactKeys(value, ["blobId", "name", "mime", "size", "hash", "createdAt"], "Inbox attachment");
  const hash = boundedText(value.hash, "attachment hash", 80, false);
  if (!/^sha256:[a-f0-9]{64}$/u.test(hash)) throw new Error("Inbox attachment hash is invalid.");
  const blobId = boundedText(value.blobId, "attachment blob id", 128, false);
  if (blobId !== `blob_${hash.slice("sha256:".length)}`) throw new Error("Inbox attachment id does not match its hash.");
  const size = value.size;
  if (typeof size !== "number" || !Number.isSafeInteger(size) || size < 0) {
    throw new Error("Inbox attachment size is invalid.");
  }
  const createdAt = boundedText(value.createdAt, "attachment createdAt", 64, false);
  const parsedDate = new Date(createdAt);
  if (!Number.isFinite(parsedDate.valueOf()) || parsedDate.toISOString() !== createdAt) {
    throw new Error("Inbox attachment createdAt is invalid.");
  }
  return {
    blobId,
    name: boundedText(value.name, "attachment name", 1_024, false),
    mime: boundedText(value.mime, "attachment mime", 255, false),
    size,
    hash,
    createdAt,
  };
}

function parseRequiredWebUrl(value: unknown): string {
  if (typeof value !== "string") throw new Error("Inbox source URL is invalid.");
  const parsed = parseWebUrl(value);
  if (parsed === null) throw new Error("Inbox source URL is invalid.");
  return parsed;
}

function boundedText(value: unknown, field: string, maximumBytes: number, allowEmpty: boolean): string {
  if (typeof value !== "string" || (!allowEmpty && value.length === 0)) {
    throw new Error(`Inbox ${field} is invalid.`);
  }
  if (new TextEncoder().encode(value).byteLength > maximumBytes) {
    throw new Error(`Inbox ${field} is too large.`);
  }
  return value;
}

function isInboxKind(value: unknown): value is InboxKind {
  return value === "text" || value === "url" || value === "image" || value === "file";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function assertExactKeys(value: Record<string, unknown>, expected: readonly string[], label: string): void {
  const allowed = new Set(expected);
  if (Object.keys(value).length !== expected.length || Object.keys(value).some((key) => !allowed.has(key))) {
    throw new Error(`${label} fields are invalid.`);
  }
}
